import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as interfaces from 'static/interfaces'
import * as constants from 'static/constants'
import { CheckBox, Button } from 'components/atoms'
import { IFilterOptions } from 'utils/weeklyTypes'
import { PostsSelector } from 'components/atoms/PostsSelector'
declare var window: interfaces.IWindow

interface IPostOption {
  value: number
  label: string
}

interface IProps {
  posts: any[]
  filterOptions: IFilterOptions
  setFilterOptions: React.Dispatch<React.SetStateAction<any>>
}

const FilterModalBody: React.VFC<IProps> = React.memo(
  ({ posts, filterOptions, setFilterOptions }) => {
    const [postIds, setPostIds] = React.useState(filterOptions.post_ids)
    const [postTypes, setPostTypes] = React.useState(filterOptions.post_types)
    const [tourGroupStatus, setTourGroupStatus] = React.useState(filterOptions.tour_group_status)
    const [alert, setAlert] = React.useState(filterOptions.alert)
    const [insufficientWithGuide, setInsufficientWithGuide] = React.useState(
      filterOptions.insufficient_with_guide
    )
    const [insufficientWithNoGuide, setInsufficientWithNoGuide] = React.useState(
      filterOptions.insufficient_with_no_guide
    )
    const [connected, setConnected] = React.useState(filterOptions.connected)
    const [notConnected, setNotConnected] = React.useState(filterOptions.not_connected)
    const [allTaskDone, setAllTaskDone] = React.useState(filterOptions.all_task_done)
    const [hasTask, setHasTask] = React.useState(filterOptions.has_task)

    const handleChangePostIds = (selected: number[]) => {
      setPostIds(selected)
    }

    // ツアータイプ選択時はツアー種類の選択肢も対象タイプに絞る
    const selectablePosts = React.useMemo(() => {
      if (postTypes.length === 0) {
        return posts
      }
      return posts.filter(post => postTypes.includes(post.post_type))
    }, [posts, postTypes])

    const handleCheckPostType = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        const newPostTypes = e.target.checked
          ? postTypes.concat([e.target.value])
          : postTypes.filter(postType => postType !== e.target.value)
        setPostTypes(newPostTypes)
        // 選択したタイプの対象外になったツアー種類は選択から外す
        if (newPostTypes.length > 0) {
          setPostIds(prevPostIds =>
            prevPostIds.filter(id => {
              const post = posts.find(p => p.id === id)
              return post && newPostTypes.includes(post.post_type)
            })
          )
        }
      },
      [postTypes, posts]
    )

    const handleCheckStatus = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        const newStatuses = e.target.checked
          ? tourGroupStatus.concat([e.target.value])
          : tourGroupStatus.filter(status => status !== e.target.value)
        setTourGroupStatus(newStatuses)
      },
      [tourGroupStatus]
    )

    const handleCheckAlert = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setAlert(e.target.checked ? true : undefined)
      },
      [filterOptions, alert]
    )

    const handleCheckInsufficientWithGuide = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setInsufficientWithGuide(e.target.checked ? true : undefined)
      },
      [filterOptions, insufficientWithGuide]
    )

    const handleCheckInsufficientWithNoGuide = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setInsufficientWithNoGuide(e.target.checked ? true : undefined)
      },
      [filterOptions, insufficientWithNoGuide]
    )

    const handleCheckConnected = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setConnected(e.target.checked ? true : undefined)
      },
      [filterOptions, connected]
    )

    const handleCheckNotConnected = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setNotConnected(e.target.checked ? true : undefined)
      },
      [filterOptions, notConnected]
    )

    const handleCheckAllTaskDone = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setAllTaskDone(e.target.checked ? true : undefined)
      },
      [filterOptions, allTaskDone]
    )

    const handleCheckHasTask = React.useCallback(
      (e: { target: { checked: any; value: any } }) => {
        setHasTask(e.target.checked ? true : undefined)
      },
      [filterOptions, hasTask]
    )

    const handleConfirm = React.useCallback(() => {
      updateUrlParams()
      setFilterOptions({
        ...filterOptions,
        post_ids: postIds,
        post_types: postTypes,
        tour_group_status: tourGroupStatus,
        alert,
        insufficient_with_guide: insufficientWithGuide,
        insufficient_with_no_guide: insufficientWithNoGuide,
        connected,
        not_connected: notConnected,
        all_task_done: allTaskDone,
        has_task: hasTask,
      })
      window.globalModal.closeModal()
    }, [
      filterOptions,
      postIds,
      postTypes,
      tourGroupStatus,
      alert,
      insufficientWithGuide,
      insufficientWithNoGuide,
      connected,
      notConnected,
      allTaskDone,
      hasTask,
    ])

    const updateUrlParams = () => {
      const url = new URL(window.location.href)
      const currentUrlParams = url.searchParams
      currentUrlParams.delete('post_ids[]')
      currentUrlParams.delete('post_types[]')
      currentUrlParams.delete('tour_group_status[]')
      currentUrlParams.delete('alert')
      currentUrlParams.delete('insufficient_with_guide')
      currentUrlParams.delete('insufficient_with_no_guide')
      currentUrlParams.delete('connected')
      currentUrlParams.delete('not_connected')
      currentUrlParams.delete('all_task_done')
      currentUrlParams.delete('has_task')

      postIds.map(id => currentUrlParams.append('post_ids[]', String(id)))
      postTypes.map(postType => currentUrlParams.append('post_types[]', postType))
      tourGroupStatus.map(status => currentUrlParams.append('tour_group_status[]', status))
      if (alert !== undefined) {
        currentUrlParams.set('alert', alert === true ? 'true' : 'false')
      }
      if (insufficientWithGuide !== undefined) {
        currentUrlParams.set(
          'insufficient_with_guide',
          insufficientWithGuide === true ? 'true' : 'false'
        )
      }
      if (insufficientWithNoGuide !== undefined) {
        currentUrlParams.set(
          'insufficient_with_no_guide',
          insufficientWithNoGuide === true ? 'true' : 'false'
        )
      }
      if (connected !== undefined) {
        currentUrlParams.set('connected', connected ? 'true' : 'false')
      }
      if (notConnected !== undefined) {
        currentUrlParams.set('not_connected', notConnected ? 'true' : 'false')
      }
      if (allTaskDone !== undefined) {
        currentUrlParams.set('all_task_done', allTaskDone ? 'true' : 'false')
      }
      if (hasTask !== undefined) {
        currentUrlParams.set('has_task', hasTask ? 'true' : 'false')
      }

      url.search = currentUrlParams.toString()
      const newUrl = url.toString()
      window.history.replaceState(
        { ...window.history.state, as: newUrl, url: newUrl },
        undefined,
        newUrl
      )
    }

    return (
      <Wrapper>
        <div className="filterCategory tourType">
          <div className="type">ツアータイプ</div>
          <div className="options">
            <CheckBox
              name="post_types"
              value="private"
              label={I18n.t('post.private_tour', { locale: 'ja' })}
              showLabelInline={true}
              key="post_type_private"
              onChangeHandler={handleCheckPostType}
              checked={postTypes.includes('private')}
            />
            <CheckBox
              name="post_types"
              value="group"
              label={I18n.t('post.group_tour', { locale: 'ja' })}
              showLabelInline={true}
              key="post_type_group"
              onChangeHandler={handleCheckPostType}
              checked={postTypes.includes('group')}
            />
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">ツアー種類</div>
          <div className="postIdsSelector">
            <PostsSelector
              posts={selectablePosts}
              selectedPostIds={postIds}
              onChangePostIds={handleChangePostIds}
              placeholder="ツアー種類を選択"
            />
          </div>
        </div>
        <div className="filterCategory">
          <div className="type">ステータス</div>
          <div className="options status">
            <CheckBox
              name="statuses"
              value="ng"
              label={I18n.t('tours.weekly.tour_group_status.ng', { locale: 'ja' })}
              showLabelInline={true}
              key="status_ng"
              onChangeHandler={handleCheckStatus}
              checked={tourGroupStatus.includes('ng')}
            />
            <CheckBox
              name="statuses"
              value="alert"
              label={I18n.t('tours.weekly.tour_group_status.alert', { locale: 'ja' })}
              showLabelInline={true}
              key="status_alert"
              onChangeHandler={handleCheckStatus}
              checked={tourGroupStatus.includes('alert')}
            />
            <CheckBox
              name="alert"
              value="alert"
              label={I18n.t('tours.weekly.alert', { locale: 'ja' })}
              showLabelInline={true}
              key="alert"
              onChangeHandler={handleCheckAlert}
              checked={alert === true}
            />
            <CheckBox
              name="insufficient_with_guide"
              value="insufficient_with_guide"
              label={I18n.t('tours.weekly.insufficient_with_guide', { locale: 'ja' })}
              showLabelInline={true}
              key="insufficient_with_guide"
              onChangeHandler={handleCheckInsufficientWithGuide}
              checked={insufficientWithGuide === true}
            />
            <CheckBox
              name="insufficient_with_no_guide"
              value="insufficient_with_no_guide"
              label={I18n.t('tours.weekly.insufficient_with_no_guide', { locale: 'ja' })}
              showLabelInline={true}
              key="insufficient_with_no_guide"
              onChangeHandler={handleCheckInsufficientWithNoGuide}
              checked={insufficientWithNoGuide === true}
            />
          </div>
          <div className="options status">
            <CheckBox
              name="connected"
              value="connected"
              label={I18n.t('tours.weekly.connected', { locale: 'ja' })}
              showLabelInline={true}
              key="connected"
              onChangeHandler={handleCheckConnected}
              checked={connected === true}
            />
            <CheckBox
              name="not_connected"
              value="not_connected"
              label={I18n.t('tours.weekly.not_connected', { locale: 'ja' })}
              showLabelInline={true}
              key="not_connected"
              onChangeHandler={handleCheckNotConnected}
              checked={notConnected === true}
            />
            <CheckBox
              name="all_task_done"
              value="all_task_done"
              label={I18n.t('tours.weekly.all_task_done', { locale: 'ja' })}
              showLabelInline={true}
              key="all_task_done"
              onChangeHandler={handleCheckAllTaskDone}
              checked={allTaskDone === true}
            />
            <CheckBox
              name="has_task"
              value="has_task"
              label={I18n.t('tours.weekly.has_task', { locale: 'ja' })}
              showLabelInline={true}
              key="has_task"
              onChangeHandler={handleCheckHasTask}
              checked={hasTask === true}
            />
          </div>
        </div>
        <div className="actions">
          <div className="action">
            <Button primary={true} handleClick={handleConfirm}>
              {I18n.t('generic.enter', { locale: 'ja' })}
            </Button>
            <Button handleClick={() => window.globalModal.closeModal()}>
              {I18n.t('generic.cancel', { locale: 'ja' })}
            </Button>
          </div>
        </div>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  .filterCategory {
    margin-bottom: 40px;
    &.tourType {
      .options .CheckBox {
        margin-bottom: 0;
      }
    }
    .type {
      margin: 12px 0px;
      font-weight: bold;
      color: ${constants.COLORS.subText};
    }
    .options {
      display: flex;
      flex-wrap: wrap;
      .CheckBox {
        margin: 0 24px 24px 0;
      }
      &.status {
        .CheckBox_Main > label .CheckBox_Icon .icon.filled {
          color: #f08a0d;
        }
      }
    }
    .postIdsSelector {
      width: 100%;
      font-size: 14px;
      .subtitle {
        display: none;
      }
    }
  }
  .actions {
    text-align: center;
    .Button {
      width: 120px;
      font-size: 14px;
    }
  }
`

export default FilterModalBody
